import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from './queryKeys';
import {
  fetchPrescriptionContentApi,
  getDoctorAllTemplateApi,
  fetchTreatmentsList,
  printReferDocumentTemplateApi,
} from '../services/prescriptionService';
import {
  listPrescriptorPrescriptionsByAppointmentApi,
  showPrescriptorPrescriptionApi,
  fetchPrescriptionSpecialTemplatesApi,
  addPrescriptionSpecialTemplateApi,
  updatePrescriptionSpecialTemplateApi,
  deletePrescriptionSpecialTemplateApi,
  updatePrescriptionSpecialTemplateOrderApi,
  fetchConfigMedicationApi,
  addConfigMedicationApi,
  deleteConfigMedicationApi,
  getAllCategoriesWithTestsApi,
} from '../services/apiService';


import {
  medicationsService
} from '../services/api/medications-service.js';
import { prescriptionService } from '../services/api/prescription-service.js';

const PRESCRIPTION_TEMPLATE_CACHE_TIME = 24 * 60 * 60 * 1000;

const normalizePrescriptionTemplateRows = (response) => {
  const rows =
    response?.data?.data?.items ??
    response?.data?.items ??
    response?.data?.data ??
    response?.data ??
    [];

  return Array.isArray(rows) ? rows : [];
};

export function prescriptionTemplatesQueryOptions(name = '') {
  const normalizedName = String(name || '').trim().toLowerCase();

  return {
    queryKey: queryKeys.prescriptions.prescriptionTemplates.search(normalizedName),
    queryFn: async ({ signal }) => {
      const response = await prescriptionService.fetchTemplates(
        {
          ...(normalizedName ? { name: normalizedName } : {}),
          per_page: 50,
        },
        { signal },
      );
      return normalizePrescriptionTemplateRows(response);
    },
    staleTime: PRESCRIPTION_TEMPLATE_CACHE_TIME,
    gcTime: PRESCRIPTION_TEMPLATE_CACHE_TIME,
    meta: { persist: true },
  };
}

export function prescriptionTemplateDetailQueryOptions(templateId) {
  return {
    queryKey: queryKeys.prescriptions.prescriptionTemplates.detail(templateId),
    queryFn: async ({ signal }) => {
      const response = await prescriptionService.fetchTemplate(templateId, { signal });
      return response?.data?.data ?? response?.data ?? null;
    },
    enabled: Boolean(templateId),
    staleTime: PRESCRIPTION_TEMPLATE_CACHE_TIME,
    gcTime: PRESCRIPTION_TEMPLATE_CACHE_TIME,
    meta: { persist: true },
  };
}

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch prescriptions linked to an appointment
 */
export function usePrescriptionsByAppointment(appointmentId, options = {}) {
  return useQuery({
    queryKey: queryKeys.prescriptions.byAppointment(appointmentId),
    queryFn: async ({ signal }) => {
      const response = await listPrescriptorPrescriptionsByAppointmentApi({ appointment_id: appointmentId, signal });
      return response?.data?.data ?? response?.data ?? [];
    },
    enabled: Boolean(appointmentId),
    ...options,
  });
}

/**
 * Fetch doctor prescription templates
 */
export function usePrescriptionTemplates(doctorId, options = {}) {
  return useQuery({
    queryKey: queryKeys.prescriptions.templates(doctorId || 'all'),
    queryFn: async () => {
      const response = await getDoctorAllTemplateApi();
      return response?.data?.data ?? response?.data ?? [];
    },
    staleTime: 15 * 60 * 1000,
    ...options,
  });
}

/**
 * Fetch doctor special prescription templates (imaging, certificate, referral, lab tests, etc.)
 * Cached for 24 hours with persistent storage as these templates rarely change.
 */
export function usePrescriptionSpecialTemplates(options = {}) {
  return useQuery({
    queryKey: queryKeys.prescriptions.specialTemplates(),
    queryFn: async ({ signal }) => {
      const response = await fetchPrescriptionSpecialTemplatesApi({ signal });
      return response?.data?.data ?? response?.data ?? [];
    },
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    meta: {
      persist: true,
    },
    ...options,
  });
}

/**
 * Fetch medication configuration options (dose, unit, route, frequency, moment, meal, duration, condition, qty, renewal, etc.)
 * Cached for 24 hours with persistent storage as these configurations rarely change.
 */
export function useConfigMedication(params = undefined, options = {}) {
  return useQuery({
    queryKey: queryKeys.prescriptions.configMedication(params),
    queryFn: async ({ signal }) => {
      const response = await fetchConfigMedicationApi(params, { signal });
      return response?.data?.options ?? response?.data ?? {};
    },
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    meta: {
      persist: true,
    },
    ...options,
  });
}

/**
 * Fetch the authenticated doctor's named prescription templates.
 * Each search is persisted under its own cache key.
 */
export function usePrescriptionTemplateSearch(name = '', options = {}) {
  return useQuery({
    ...prescriptionTemplatesQueryOptions(name),
    ...options,
  });
}

/**
 * Merge every entry from a prescription template into an existing prescription.
 */
export function useMergePrescriptionTemplateEntries(options = {}) {
  const queryClient = useQueryClient();
  const { onSuccess, ...mutationOptions } = options;

  return useMutation({
    ...mutationOptions,
    mutationFn: async ({ prescriptionId, templateId }) => {
      const response = await prescriptionService.mergeTemplateEntries(
        prescriptionId,
        templateId,
      );
      return response?.data?.data ?? response?.data ?? response;
    },
    onSuccess: async (data, variables, context) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.prescriptions.detail(variables.prescriptionId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.prescriptions.lists(),
        }),
      ]);
      await onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Refresh all cached medication configuration variants, including persisted
 * queries that are not currently mounted.
 */
export function refetchConfigMedicationQueries(queryClient) {
  return queryClient.refetchQueries({
    queryKey: queryKeys.prescriptions.configMedication(),
    type: 'all',
  });
}

/**
 * Fetch all medical test categories with their tests.
 * Cached for 24 hours with persistent storage as test categories rarely change.
 * Supports optional query params (e.g. { search: '...' }).
 */
export function useCategoriesWithTests(params = undefined, options = {}) {
  return useQuery({
    queryKey: queryKeys.prescriptions.categoriesWithTests(params),
    queryFn: async ({ signal }) => {
      const response = await getAllCategoriesWithTestsApi(params, { signal });
      const raw = response?.data;
      const categories =
        (raw?.success && Array.isArray(raw?.data) ? raw.data : null) ||
        (Array.isArray(raw?.data?.data) ? raw.data.data : null) ||
        (Array.isArray(raw?.data) ? raw.data : null) ||
        (Array.isArray(raw) ? raw : []);
      return categories;
    },
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    meta: {
      persist: true,
    },
    ...options,
  });
}

/**
 * Fetch list of treatments/medicines
 */
export function useTreatmentsList(doctorId, options = {}) {
  return useQuery({
    queryKey: queryKeys.prescriptions.treatments(doctorId || 'all'),
    queryFn: async () => {
      const response = await fetchTreatmentsList();
      return response?.data?.data ?? response?.data ?? [];
    },
    staleTime: 30 * 60 * 1000,
    ...options,
  });
}

/**
 * Fetch prescription content / detail
 */
export function usePrescriptionDetail(prescriptionId, options = {}) {
  return useQuery({
    queryKey: queryKeys.prescriptions.detail(prescriptionId),
    queryFn: async ({ signal }) => {
      const response = await showPrescriptorPrescriptionApi({ id: prescriptionId, signal });
      return response?.data?.data ?? response?.data ?? null;
    },
    enabled: Boolean(prescriptionId),
    ...options,
  });
}

// ============================================================================
// Mutation Hooks (with Targeted Invalidation)
// ============================================================================

/**
 * Invalidate prescription caches after print or save
 */
export function usePrintReferDocument(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => printReferDocumentTemplateApi(variables),
    onSuccess: (data, variables) => {
      if (variables?.appointment_id) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.prescriptions.byAppointment(variables.appointment_id),
        });
      }
      options.onSuccess?.(data, variables);
    },
    ...options,
  });
}

/**
 * Add special prescription template with cache invalidation
 */
export function useAddSpecialTemplate(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values) => addPrescriptionSpecialTemplateApi(values),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.prescriptions.specialTemplates() });
      options.onSuccess?.(data, variables, context);
    },
    ...options,
  });
}

/**
 * Update special prescription template with cache invalidation
 */
export function useUpdateSpecialTemplate(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values) => updatePrescriptionSpecialTemplateApi(values),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.prescriptions.specialTemplates() });
      options.onSuccess?.(data, variables, context);
    },
    ...options,
  });
}

/**
 * Delete special prescription template with cache invalidation
 */
export function useDeleteSpecialTemplate(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values) => deletePrescriptionSpecialTemplateApi(values),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.prescriptions.specialTemplates() });
      options.onSuccess?.(data, variables, context);
    },
    ...options,
  });
}

/**
 * Update special prescription template order with cache invalidation
 */
export function useUpdateSpecialTemplateOrder(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values) => updatePrescriptionSpecialTemplateOrderApi(values),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.prescriptions.specialTemplates() });
      options.onSuccess?.(data, variables, context);
    },
    ...options,
  });
}

/**
 * Add medication configuration option with cache invalidation
 */
export function useAddConfigMedication(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values) => addConfigMedicationApi(values),
    onSuccess: async (data, variables, context) => {
      await refetchConfigMedicationQueries(queryClient);
      options.onSuccess?.(data, variables, context);
    },
    ...options,
  });
}

/**
 * Delete medication configuration option with cache invalidation
 */
export function useDeleteConfigMedication(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values) => deleteConfigMedicationApi(values),
    onSuccess: async (data, variables, context) => {
      await refetchConfigMedicationQueries(queryClient);
      options.onSuccess?.(data, variables, context);
    },
    ...options,
  });
}


/**
 * fetch medications list and store it
 */

export function useMedicationList(options = {}) {
  return useQuery({
    queryKey: queryKeys.medications.list(),
    queryFn: async () => {
      const response = await medicationsService.fetchAllMedications()
      return response
    },
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    meta: {
      persist: true,
    },
    ...options,
  });
}
